"use client";

import { useQuery } from "@tanstack/react-query";

import type { BuilderCvCheck } from "@/types/builder-cv-check";
import type { ResumeDocument } from "@/types/cv";

type BuilderCvCheckLanguage = "Indonesian" | "English";

type UseBuilderCvCheckOptions = {
  enabled?: boolean;
  outputLanguage?: BuilderCvCheckLanguage;
};

function createResumeSignature(resume: ResumeDocument) {
  const serialized = JSON.stringify(resume);
  let hash = 0;

  for (let index = 0; index < serialized.length; index += 1) {
    hash = (hash * 31 + serialized.charCodeAt(index)) >>> 0;
  }

  return `${serialized.length}:${hash.toString(36)}`;
}

export async function fetchBuilderCvCheck(
  resume: ResumeDocument,
  outputLanguage: BuilderCvCheckLanguage,
): Promise<BuilderCvCheck> {
  const response = await fetch("/api/cv-builder/check", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ resume, outputLanguage }),
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      typeof payload?.error === "string"
        ? payload.error
        : "Failed to check CV";
    throw new Error(message);
  }

  return payload as BuilderCvCheck;
}

export function useBuilderCvCheck(
  resume: ResumeDocument | null,
  options: UseBuilderCvCheckOptions = {},
) {
  const outputLanguage = options.outputLanguage ?? "Indonesian";
  const resumeSignature = resume ? createResumeSignature(resume) : "empty";

  return useQuery({
    queryKey: ["builderCvCheck", outputLanguage, resumeSignature],
    queryFn: () => {
      if (!resume) {
        throw new Error("Resume is required for CV check");
      }

      return fetchBuilderCvCheck(resume, outputLanguage);
    },
    enabled: Boolean(resume) && (options.enabled ?? true),
    staleTime: 30 * 60 * 1000,
  });
}
